import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import WeeklyDateSwiper from '../../../../components/home/WeeklyDateSwiper';
import RecommendBox from '../../../../components/home/RecommendBox';
import MealRecordCard from '../../../../components/home/food-record/MealRecordCard';
import NutritionStat from '../../../../components/home/food-record/NutritionStat';

import URL from '../../../../constants/url';
import { markBackTarget } from '../../../../hook/useBackTo';
import style from './FoodRecordMain.module.css';

import { getCommCdListNew } from '../../../../assets/js/comm';

import axiosApi from '../../../../api/daonAxios';


// ───── Mock 데이터 (API 연동 전 퍼블 검증용) ─────────────────────
// const MOCK_DAY_MARKS = {
//     // 'YYYY-MM-DD': 'good' | 'warn'
//     '20260806': 'good',
//     '20260805': 'warn',
// };


const MIN_SKELETON_MS = 600;


const MARK_DAYS = 41;

const toRecDe = (dateStr) => String(dateStr).replace(/-/g, '');

const FoodRecordMain = () => {
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(dayjs().startOf('day'));

    const dateKey = selectedDate.format('YYYYMMDD');

    const [ntktmTyCds, setNtktmTyCds] = useState([]);

    const [toDayCalorHist, setToDayCalorHist] = useState([]);
    const [mealLoading, setMealLoading] = useState(true);
    const skeletonTimerRef = useRef(null);

    const [dayMarks, setDayMarks] = useState({}); // 날짜 바 점

    const [toDayCalorTotal, setToDayCalorTotal] = useState({});

    const aiRequestRef = useRef(false);
    const aiPollingIdRef = useRef(null);
    const aiPollingTimerRef = useRef(null);

    const [aiComment, setAiComment] = useState('');
    const [aiCommentLoading, setAiCommentLoading] = useState(false);

    useEffect(() => {
        markBackTarget(URL.FOOD_RECORD_MAIN);
    }, []);

    useEffect(() => {
        return () => {
            clearTimeout(skeletonTimerRef.current);
            clearTimeout(aiPollingTimerRef.current);

            aiPollingIdRef.current = null;
            aiRequestRef.current = false;
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {

            await getCommCdListNew(['NTKTM_TY_CD'])
            .then( (res) => {
                setNtktmTyCds([...res.NTKTM_TY_CD]);
            })
            .catch( (err) => {
               console.log(" =======> getHospitalCodeList Error : ", err );
            });
        }
        fetchData();
    },[])



    useEffect(() => {
        const startDe = toRecDe(selectedDate.subtract(MARK_DAYS, 'day').format('YYYY-MM-DD'));
        const endDe = dayjs().startOf('day').format('YYYYMMDD');
        const recDe = selectedDate.format('YYYYMMDD');

        if (dayjs().format('YYYYMMDD') !== recDe) {

            if (aiPollingTimerRef.current) {
                clearTimeout(aiPollingTimerRef.current);
                aiPollingTimerRef.current = null;
            }

            aiPollingIdRef.current = null;
            aiRequestRef.current = false;
            setAiCommentLoading(false);
        }
        
        getDietMainDate(recDe, startDe, endDe);

    },[selectedDate]);

    const getDietMainDate = async (recDe, startDe, endDe) => {
        const jToken = localStorage.getItem("jToken");

        setMealLoading(true);
        const startedAt = Date.now();

        const requestOption = {
            params : {
                recDe : recDe,
                startDe : toRecDe(selectedDate.subtract(MARK_DAYS, 'day').format('YYYY-MM-DD')),
                endDe : endDe,
            },
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        await axiosApi.get('/diet/main',requestOption)
        .then((res) => {
            if(res.status === 200) {
                const usrCalorList = res.data.result.usrCalorList;
                setToDayCalorHist(usrCalorList);

                const calorRecList = usrCalorList.flatMap(item => item.calorRecList ?? []);

                const dietDayInfo = res.data.result.dietDayInfo ?? {};
                const nutritionGoal = res.data.result.nutritionGoal ?? {};

                // 소수점 둘째 자리에서 반올림
                const round1 = (value) =>
                    Math.round(Number(value ?? 0) * 10) / 10;

                // 끼니별 합산 → 끼니별 반올림 → 하루 전체 합산
                const sumMealNutrition = (key, digits = 1) => {

                    const round = (value) =>
                        digits === 0
                            ? Math.round(Number(value ?? 0))
                            : round1(value);

                    const total = usrCalorList.reduce((total, meal) => {

                        // 해당 끼니의 음식 영양값 합산
                        const mealTotal = (meal.calorRecList ?? []).reduce(
                            (acc, cur) => acc + Number(cur[key] ?? 0),
                            0
                        );

                        // 끼니별 반올림 후 누적
                        return total + round(mealTotal);

                    }, 0);

                    return round(total);
                };

                const nutrition = {
                    energy: {
                        value: sumMealNutrition('energy', 0),
                        goal: Number(nutritionGoal.energyGoal) || 0,
                        unit: 'kcal'
                    },
                    sodium: {
                        value: sumMealNutrition('sodium', 0),
                        goal: Number(nutritionGoal.sodiumGoal) || 0,
                        unit: 'mg'
                    },
                    protein: {
                        value: sumMealNutrition('protein', 1),
                        goal: Number(nutritionGoal.proteinGoal) || 0,
                        unit: 'g'
                    },
                    carbohydrate: {
                        value: sumMealNutrition('carbohydrate', 1),
                        goal: Number(nutritionGoal.carbohydrateGoal) || 0,
                        unit: 'g'
                    },
                    fat: {
                        value: sumMealNutrition('fat', 1),
                        goal: Number(nutritionGoal.fatGoal) || 0,
                        unit: 'g'
                    }
                };
                setToDayCalorTotal(nutrition);

                const dietPeriodInfo = res.data.result.dietPeriodInfo ?? [];

                const marks = dietPeriodInfo.reduce((acc, item) => {
                    const isWarn =
                        Number(item.energy || 0) > Number(nutritionGoal.energyGoal || 0) ||
                        Number(item.sodium || 0) > Number(nutritionGoal.sodiumGoal || 0) ||
                        Number(item.protein || 0) > Number(nutritionGoal.proteinGoal || 0) ||
                        Number(item.carbohydrate || 0) > Number(nutritionGoal.carbohydrateGoal || 0) ||
                        Number(item.fat || 0) > Number(nutritionGoal.fatGoal || 0);

                    acc[item.recDe] = isWarn ? 'warn' : 'good';

                    return acc;
                }, {});

                setDayMarks(marks);

                const today = dayjs().format('YYYYMMDD');
                if (today === recDe) {
                    debugger;
                    const toDayAiComment = res.data.result.toDayAiComment || {};
    
                    if (toDayAiComment?.aiStatCd === 'S') {
    
                        // 이미 완료된 결과
                        setAiComment(toDayAiComment.aiAsstMsg ?? '');
                        setAiCommentLoading(false);
    
                    } else if (toDayAiComment?.aiStatCd === 'W') {
    
                        // 이전 요청이 아직 처리 중
                        setAiCommentLoading(true);
                        aiRequestRef.current = true;
    
                        startAiPolling(toDayAiComment.aiAsstMsgId);
    
                    } else {
    
                        // 현재 식단 버전에 해당하는 AI 요청 자체가 없음
                        if (today === recDe) {
                            getAiComment(usrCalorList, recDe, nutritionGoal);
                        }
                    }
                }


            } else {
                console.log("~?")
            }
        })
        .catch(error => {
            console.error(error);
        }).finally(() => {

            // 응답이 빨라도 최소 시간은 스켈레톤 유지
            const elapsed = Date.now() - startedAt;
            const remain = Math.max(0, MIN_SKELETON_MS - elapsed);

            clearTimeout(skeletonTimerRef.current);
            skeletonTimerRef.current = setTimeout(() => setMealLoading(false), remain);
        })
    }

    const getAiComment = async (usrCalorList, recDe, nutritionGoal) => {

        if (aiRequestRef.current) {
            return;
        }

        aiRequestRef.current = true;
        setAiCommentLoading(true);

        const aiDietData = makeAiDietData(usrCalorList, recDe, nutritionGoal);


        const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));
        // 어느 병원인지 알 수 없어서 일단 012 하드코딩
        const patientId = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012")?.patientId;

        const jToken = localStorage.getItem("jToken");
        const reqOptions = { 
            headers: { Authorization: `Bearer ${jToken}` } ,
            noLoading: true,
        };

        const requestData = {
            recDe,
            patientId,
            drOrglData: JSON.stringify(aiDietData)
        };

        try {

            const res = await axiosApi.post('/diet/aiComment', requestData, reqOptions);

            const aiAsstMsgId = res.data.result;
            if (aiAsstMsgId) {
                // 신규 AI 요청이 생성됨
                startAiPolling(aiAsstMsgId);
            } else {
                // 신규 요청 없음
                aiRequestRef.current = false;
                setAiCommentLoading(false);
            }

        } catch (error) {

            console.error('AI Comment 오류:', error);

            // 요청 자체가 실패했으면 재시도 가능
            aiRequestRef.current = false;
            setAiCommentLoading(false);
        }
    }

    const startAiPolling = (aiAsstMsgId) => {

        if (!aiAsstMsgId) {
            return;
        }

        // 이미 같은 ID를 폴링 중이면 중복 시작 방지
        if (aiPollingIdRef.current === aiAsstMsgId) {
            return;
        }

        // 기존 타이머 제거
        if (aiPollingTimerRef.current) {
            clearTimeout(aiPollingTimerRef.current);
        }

        aiPollingIdRef.current = aiAsstMsgId;

        pollAiComment(aiAsstMsgId);
    };

    const pollAiComment = async (aiAsstMsgId, attempt = 0) => {

        // 이미 중단되었거나 다른 ID의 폴링으로 변경된 경우 종료
        if (aiPollingIdRef.current !== aiAsstMsgId) {
            return;
        }

        const MAX_ATTEMPTS = 15;
        const POLLING_INTERVAL = 2000;

        if (attempt >= MAX_ATTEMPTS) {
            console.warn('AI Comment polling timeout');
            aiRequestRef.current = false;
            aiPollingIdRef.current = null;
            setAiCommentLoading(false);
            return;
        }

        const jToken = localStorage.getItem("jToken");

        try {

            const res = await axiosApi.get(
                '/diet/aiComment/result',
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    },
                    params: {
                        aiAsstMsgId
                    },
                    noLoading: true,
                }
            );

            if (aiPollingIdRef.current !== aiAsstMsgId) {
                return;
            }

            const result = res.data?.result;

            console.log('AI Comment polling:', result);

            // 현재 식단 버전과 맞지 않는 요청
            if (!result) {
                aiRequestRef.current = false;
                aiPollingIdRef.current = null;
                setAiCommentLoading(false);
                return;
            }

            // 완료
            if (result.aiStatCd === 'S') {

                setAiComment(result.aiAsstMsg ?? '');
                setAiCommentLoading(false);

                aiRequestRef.current = false;
                aiPollingIdRef.current = null;

                return;
            }

            // 실패
            if (result.aiStatCd === 'F') {

                console.error('AI Comment 처리 실패');

                aiRequestRef.current = false;
                aiPollingIdRef.current = null;
                setAiCommentLoading(false);

                return;
            }

            // W 상태면 2초 후 다시 조회
            aiPollingTimerRef.current = setTimeout(() => {
                pollAiComment(aiAsstMsgId, attempt + 1);
            }, POLLING_INTERVAL);

        } catch (error) {

            console.error('AI Comment polling 오류:', error);
            aiRequestRef.current = false;
            aiPollingIdRef.current = null;
            setAiCommentLoading(false);
        }
    };

    
    const makeAiDietData = (usrCalorList, recDe, nutritionGoal) => {
        const meals = (usrCalorList ?? [])
            .filter(meal => (meal.calorRecList ?? []).length > 0)
            .map((meal) => {

                const calorRecList = meal.calorRecList ?? [];

                const totals = calorRecList.reduce(
                    (acc, cur) => {
                        acc.energy += Number(cur.energy) || 0;
                        acc.protein += Number(cur.protein) || 0;
                        acc.sodium += Number(cur.sodium) || 0;

                        // 미확인 영양소 누적 (하나라도 1이면 미확인)
                        if (cur.energyUnknownAt === '1') {
                            acc.incompleteNutrients.add('energy');
                        }

                        if (cur.proteinUnknownAt === '1') {
                            acc.incompleteNutrients.add('protein');
                        }

                        if (cur.sodiumUnknownAt === '1') {
                            acc.incompleteNutrients.add('sodium');
                        }

                        return acc;
                    },
                    {
                        energy: 0,
                        protein: 0,
                        sodium: 0,
                        incompleteNutrients: new Set()
                    }
                );

                return {
                    energy: Math.round(totals.energy * 100) / 100,
                    meal_type: meal.ntktmTyCd,
                    protein: Math.round(totals.protein * 100) / 100,
                    record_count: calorRecList.length,
                    sodium: Math.round(totals.sodium * 100) / 100,
                    incomplete_nutrients: [...totals.incompleteNutrients]
                };
            });

        return {
            criteria: {
                sodium_limit_mg: nutritionGoal.sodiumGoal,
                protein_g: nutritionGoal.proteinGoal,
                energy_kcal: nutritionGoal.energyGoal,
                fat_g: nutritionGoal.fatGoal,
                carbohydrate_g: nutritionGoal.carbohydrateGoal,
            },
            evaluated_at: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
            meals,
            nutrition_calculation_complete: true,
            record_date: dayjs(recDe, 'YYYYMMDD').format('YYYY-MM-DD')
        };
    };


    // 빈 끼니 카드의 + 버튼 → 바로 촬영으로
    const handleAddMeal = (type) => {
        navigate(`${URL.FOOD_PHOTO_CAPTURE}?meal=${type}&date=${dateKey}`);
    };

    const handleAiChat = useCallback(() => {
        navigate(URL.CHATBOT , { state: { mode: 'diet', aiComment : aiComment } });
    },[aiComment]);

     const handleOpenDetail = (ntktmTyCd, isEmpty) => {
        if(isEmpty) {
            navigate(`${URL.FOOD_PHOTO_CAPTURE}?meal=${ntktmTyCd}&date=${dateKey}`);
            // navigate(URL.FOOD_SEARCH, {state: {saveGbn : "I", ntktmTyCd : ntktmTyCd, recDe : dateKey}});
            return;
        } 

        navigate(URL.FOOD_RECORD_DETAIL, {state: {ntktmTyCd : ntktmTyCd, recDe : dateKey}});
     };

    return (
        <>
            <HeadSub title="식단 기록" />

            <WeeklyDateSwiper
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                dayMarks={dayMarks}
            />

            {/* <RecommendBox onClick={handleAiChat} /> */}

            {dayjs().format('YYYYMMDD') === dateKey &&
                <RecommendBox
                    description={ aiCommentLoading ? 'AI가 오늘의 식단을 분석하고 있습니다.' : aiComment }
                    onClick={handleAiChat}
                />
            }

            <div id="sub-content" data-top-margin="0">
                <div className="inner-wrap" data-top-padding="0">    
                    
                    <section className={style.section}>
                        <h3 className={style.sectionTitle}>오늘의 식단 기록</h3>
                        <div className={style.mealList}>
                            {/* {ntktmTyCds.map((item) => {
                                const isEmpty = toDayCalorHist.filter(i => i.ntktmTyCd === item.value).length === 0;
                                return (
                                    <MealRecordCard
                                        key={item.value}
                                        label={item.label}
                                        datas={toDayCalorHist.filter(i => i.ntktmTyCd === item.value)}
                                        onAdd={() => handleAddMeal(item.value, isEmpty)}
                                        onClick={() => handleOpenDetail(item.value, isEmpty)}
                                    />
                                )
                            })} */}

                            {/* 스켈레톤 로딩 3개 고정 */}
                            {(mealLoading || ntktmTyCds.length === 0) ? (
                                [0, 1, 2, 3].map((i) => (
                                    <MealRecordCard key={`sk_${i}`} loading />
                                ))
                            ) : (

                            ntktmTyCds.map((item) => {
                                const mealData = toDayCalorHist.find(
                                    i => i.ntktmTyCd === item.value
                                );
                                const isEmpty = !mealData?.calorRecList?.length;

                                return (
                                    <MealRecordCard
                                        key={item.value}
                                        label={item.label}
                                        datas={mealData ? [mealData] : []}
                                        onAdd={() => handleAddMeal(item.value)}
                                        onClick={() => handleOpenDetail(item.value, isEmpty)}
                                    />
                                );
                            })
                            )}
                        </div>
                    </section>

                    <section className={style.section}>
                        <div className={style.sectionHead}>
                            <h3 className={style.sectionTitle}>오늘의 섭취 현황</h3>
                            <div className={style.legend} aria-hidden="true">
                                <span className={`${style.legendDot} ${style.legendWarn}`} />주의
                                <span className={`${style.legendDot} ${style.legendGood}`} />양호
                            </div>
                        </div>
                        <NutritionStat data={toDayCalorTotal} />
                    </section>
                    
                </div>
            </div>
        </>
    );
};

export default FoodRecordMain;